import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight, Fingerprint } from 'lucide-react';
import type { Takeover } from './chat/AuthChatPanel';
import { AuthLayout } from './AuthLayout';
import { AuthDivider, AuthError, AuthField, AuthHeading, AuthPasswordField } from './AuthControls';
import { AuthPrimaryButton, AuthSecondaryButton, authLinkClass } from './AuthControls';
import { useForm } from 'hooks/useForm';
import { useAuth } from 'hooks/useAuth';
import { isInternalCtaUrl } from 'helpers/announcement-url';
import { trackUmami } from '@/lib/umami/umami';
import { signinFailureReason } from './signin-failure-reason';

type StateType = {
  email: string;
  password: string;
};

type Pending = 'password' | 'passkey' | null;

// Deliberately loose. This exists to catch a typed address with no @ before it
// costs a round trip, not to adjudicate what a mailbox may be called.
const isEmailish = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * Where to land after signing in. `from` comes off the query string, and while the
 * app only ever writes a local pathname into it, anyone can put anything there:
 * /auth/signin?from=https://evil.example.com would otherwise hand the visitor
 * straight to that host the moment their password worked.
 *
 * isInternalCtaUrl is the same predicate the announcement CTA uses. It is named for
 * that caller but the question is identical — does this resolve to our own origin —
 * and it settles protocol relative and backslash forms too.
 */
const safeRedirect = (from: string | null): string => (from && isInternalCtaUrl(from) ? from : '/');

// What useClient throws: { status, message } for anything over 400. A failed
// WebAuthn ceremony throws a DOMException instead, which has a name and no status.
type ApiError = { status?: number; message?: string; name?: string };

const isCeremonyError = (ex: unknown): boolean => {
  const { status, name } = (ex ?? {}) as ApiError;
  return !status && !!name;
};

/**
 * 401 is the only signin failure the user can fix by retyping, and it arrives as
 * the bare string "Unauthorized", so it gets the wording. Everything else — a rate
 * limit, a server fault — comes with a reason, and relabelling those as a bad
 * password sends someone in a circle retyping a password that was right the first
 * time.
 *
 * Note that a non-Active account is *not* in the second group: signin.ts throws a
 * bare UNAUTHORIZED for a blocked user exactly as it does for a wrong password, so
 * there is no account-status oracle here. Keep it that way.
 */
const readSigninError = (ex: unknown): string => {
  const { status, message } = (ex ?? {}) as ApiError;
  if (status === 401) return 'Incorrect email or password. Please try again.';
  if (message) return message;
  return 'Could not sign you in. Please try again.';
};

/**
 * A passkey can fail for a reason the user can act on, so the cases are worth
 * separating. NotAllowedError is what the browser raises both when the prompt is
 * dismissed and when no credential on the device matches this site, which is the
 * normal outcome when signing in from an origin the passkey was not registered
 * against.
 */
const readPasskeyError = (ex: unknown): string => {
  const { status, message, name } = (ex ?? {}) as ApiError;
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return 'No passkey was used. If you have not set one up for this address on this site, sign in with your password.';
  }
  if (status === 401) return 'That passkey could not be verified. Sign in with your password instead.';
  if (message) return message;
  return 'That passkey could not be verified. Sign in with your password instead.';
};

/**
 * The sign-in heading, error and form, without the page around it. SigninScreen wraps it
 * in the auth layout; the accept invitation screen swaps it in under its "account active"
 * panel, so that panel slides away onto a form that is already ready.
 */
export const SigninForm = () => {
  const [pending, setPending] = useState<Pending>(null);
  // Two sources, kept apart on purpose. Blurring the email field must not wipe
  // "incorrect email or password", and a server failure must not be cleared by
  // tabbing back through a field that is already valid.
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const { signin, signinWithPasskey } = useAuth();
  const { state, formRef, update } = useForm<StateType>();
  const { email, password } = state;
  const searchParams = new URLSearchParams(window.location.search);
  const destination = safeRedirect(searchParams.get('from'));

  const onSubmit = async () => {
    if (isDisabled) return;
    setServerError(null);
    setPending('password');
    trackUmami('signin-attempted', { method: 'password' });
    try {
      if (!email || !password) throw new Error('Email and password are required');
      await signin({ email, password });
      trackUmami('signin-succeeded', { method: 'password' });
      window.location.href = destination;
    } catch (ex) {
      trackUmami('signin-failed', { method: 'password', reason: signinFailureReason(ex, 'password') });
      // signin() finishes inside a WebAuthn prompt for anyone whose account
      // requires a passkey after the password, so this catch sees ceremony
      // failures too. Those mean the password was accepted — keep it, and say what
      // actually went wrong rather than calling it a bad password.
      if (isCeremonyError(ex)) {
        setServerError(readPasskeyError(ex));
      } else {
        // Retyping an address you got right is the most irritating way to be told
        // a password was wrong, so only the password is cleared.
        update({ email, password: '' });
        setServerError(readSigninError(ex));
      }
    } finally {
      setPending(null);
    }
  };

  /**
   * The passkey routes are keyed on the email address, so this needs one before it
   * can ask the browser for anything — hence a prompt rather than a hidden button.
   * "Enter your email first" is a smaller surprise than a control that silently
   * does nothing.
   */
  const onPasskey = async () => {
    if (pending) return;
    if (!email || !isEmailish(email)) {
      trackUmami('signin-failed', { method: 'passkey', reason: 'no-email' });
      setServerError('Enter your email address above to sign in with your passkey.');
      // Take the visitor to the field it is asking for.
      formRef.current?.querySelector<HTMLInputElement>('input[name="email"]')?.focus();
      return;
    }

    setServerError(null);
    setPending('passkey');
    trackUmami('signin-attempted', { method: 'passkey' });
    try {
      await signinWithPasskey({ email });
      trackUmami('signin-succeeded', { method: 'passkey' });
      window.location.href = destination;
    } catch (ex) {
      trackUmami('signin-failed', { method: 'passkey', reason: signinFailureReason(ex, 'passkey') });
      setServerError(readPasskeyError(ex));
    } finally {
      setPending(null);
    }
  };

  const onEmailBlur = () =>
    setEmailError(!email || isEmailish(email) ? null : 'That does not look like an email address.');

  const isDisabled = !!pending || !email || !password;
  const error = serverError ?? emailError;

  return (
    <>
      <AuthHeading title="Welcome back" subtitle="Sign in to your Pertento workspace." />

      {!!error && <AuthError>{error}</AuthError>}

      {/* Reads top to bottom the way it works. The email comes first because both
          ways in need it: the passkey routes are keyed on the address (see
          passkey-router.ts), so a passkey button above the email field presented the
          email as an alternative when it is a prerequisite. The passkey is still the
          primary action and the password the fallback.

          When passkeys can sign in without an email, the button can move back above
          the field and the divider can say "or use your email" again. */}
      <form onSubmit={onSubmit} ref={formRef} className="flex flex-col gap-[18px]">
        <AuthField
          label="Email address"
          type="email"
          name="email"
          autoComplete="username"
          placeholder="you@company.com"
          onBlur={onEmailBlur}
        />

        <div className="flex flex-col items-center gap-2.5">
          <AuthPrimaryButton
            type="button"
            icon={<Fingerprint size={18} strokeWidth={1.5} />}
            onClick={onPasskey}
            disabled={!!pending}
            isPending={pending === 'passkey'}
          >
            {pending === 'passkey' ? 'Waiting for your passkey…' : 'Sign in with a passkey'}
          </AuthPrimaryButton>
          <span className="text-dim text-micro">
            Uses the email above, with your face, fingerprint or security key.
          </span>
        </div>

        <AuthDivider label="or use your password" />

        <AuthPasswordField
          label="Password"
          name="password"
          autoComplete="current-password"
          action={
            <Link to="/auth/forgot-password" className={`${authLinkClass} font-medium`}>
              Forgot password?
            </Link>
          }
        />

        <AuthSecondaryButton
          type="submit"
          disabled={isDisabled}
          isPending={pending === 'password'}
          trailingIcon={<ArrowRight size={16} strokeWidth={1.5} />}
        >
          {pending === 'password' ? 'Working…' : 'Sign in with password'}
        </AuthSecondaryButton>
      </form>
    </>
  );
};

type SigninScreenProps = {
  /** Open the signup chat on mount. The /auth/signup route is this screen, opened. */
  startInSignup?: boolean;
  /** Full screen content over the page on mount, e.g. the email verified screen. */
  takeover?: Takeover;
  onClosed?: () => void;
};

type SigninFooterApi = { openSignup: () => void };

export const renderSigninFooter = ({ openSignup }: SigninFooterApi) => (
  <span>
    New to Pertento?{' '}
    <button type="button" onClick={openSignup} className={`${authLinkClass} font-medium`}>
      Create an account
    </button>
  </span>
);

export const SigninScreen = ({ startInSignup = false, takeover, onClosed }: SigninScreenProps) => (
  <AuthLayout startInSignup={startInSignup} takeover={takeover} onClosed={onClosed} footer={renderSigninFooter}>
    <SigninForm />
  </AuthLayout>
);
